import { ApiError } from "../api/types";

/**
 * `errorCopy` — what a Cerebr8 CLIENT is allowed to read when something fails.
 *
 * ## Why this file exists
 *
 * A client opened `/w/:slug/admin` and was shown, verbatim:
 *
 * ```
 * Could not load users
 * Could not load the matrix
 * ⚠ Panel "access" failed to render
 * An unexpected error occurred.
 * ```
 *
 * Four lines, three of them written for whoever wrote the code. "Matrix" is the
 * internal name of a roles × capabilities grid; "panel" and "render" are React
 * words; and "An unexpected error occurred" is what a UI says when it has NOTHING
 * — which was literally true, and is the first defect this module fixes.
 *
 * ## The empty message was a real bug, not a wording choice
 *
 * `realAdapter` built its `ApiError` message as
 * `data.message ?? res.statusText`. The edge serves **HTTP/2**, and HTTP/2 has no
 * reason phrase — `Response.statusText` is the EMPTY STRING for every response.
 * elaborate's own 404 body is `{"error":"not_found"}`, which carries neither
 * `code` nor `message`, so every 404 became `new ApiError(404, "http_404", "")`.
 * Every renderer downstream then printed its own "something went wrong" fallback,
 * and no one could tell a missing route from a broken one.
 *
 * {@link statusSentence} is the floor under that: a message is never empty, and
 * what fills it is a sentence, never a status code.
 *
 * ## The rule this module encodes
 *
 * ⛔ A raw error code, an HTTP number, a route, an internal noun (`matrix`,
 * `panel`, `payload`, `manifest`, `adapter`) never reaches a rendered string.
 * Those belong in the console and in the correlation id an operator can search
 * for — `@norkron/web-errors` already carries that half.
 */

/**
 * A plain sentence for a status the server gave no message for.
 *
 * Deliberately NOT exhaustive per code. A client cannot act on the difference
 * between 502 and 504, so both say the same true thing; the codes that DO imply a
 * different next step for the reader (you are signed out / you may not / it is not
 * there / it is too big / slow down) each get their own.
 */
export function statusSentence(status: number): string {
  if (status === 0) return "We couldn't reach the server. Check your connection and try again.";
  if (status === 401) return "Your session has ended. Sign in again to continue.";
  if (status === 403) return "You don't have permission to do that.";
  if (status === 404) return "That isn't available here.";
  if (status === 409) return "Someone else changed this first. Reload and try again.";
  if (status === 413) return "That file is too large.";
  if (status === 429) return "Too many requests just now. Wait a moment and try again.";
  if (status >= 500) return "The server had a problem. Nothing was changed — try again in a moment.";
  if (status >= 400) return "That request couldn't be completed.";
  return "Something went wrong.";
}

/**
 * ⛔ THE ONE PLACE A THROWN VALUE BECOMES WORDS ON A CLIENT'S SCREEN.
 *
 * `fallback` is what to say when the thrown thing carries no message a human
 * wrote — never `String(e)`, never `e.stack`, never a code. An `ApiError` whose
 * message came from the server is trusted (the server's error copy is written for
 * the reader); an `ApiError` with an EMPTY message — the HTTP/2 case above —
 * degrades to {@link statusSentence}, not to the empty string.
 */
export function readableError(e: unknown, fallback: string): string {
  if (e instanceof ApiError) {
    // ⛔ STATUS 0 MEANS THERE WAS NO RESPONSE, so there is no server-authored
    // message to trust — `realAdapter` fills that slot with `(e as Error).message`,
    // i.e. whatever the JS engine calls it ("Failed to fetch" on Chrome, "Load
    // failed" on Safari). Both contain a space, so both used to pass the
    // is-this-a-sentence test below and reach the reader verbatim, which made
    // `statusSentence(0)` dead code and told a client who lost wifi "Load failed".
    if (e.status === 0) return statusSentence(0);
    const m = e.message.trim();
    if (m.length > 0 && !isCodeShaped(m)) return m;
    return statusSentence(e.status);
  }
  return fallback;
}

/**
 * Does this string look like an identifier rather than a sentence?
 *
 * `not_found`, `http_404`, `PA_UNSERVED_OPERATION` — a server that puts its code
 * in the message slot must not have it forwarded verbatim. A real sentence has a
 * space in it; a code does not.
 */
function isCodeShaped(message: string): boolean {
  return !message.includes(" ");
}

/**
 * Is this failure "elaborate does not serve this surface here", as opposed to
 * "this surface is broken"?
 *
 * ⚠️ SCOPE. Only meaningful for a request that addresses a whole SURFACE — the
 * admin console's first read, the access console's first read. A 404 on
 * `/api/documents/:id` means "that document is gone", which is a completely
 * different sentence, so this must never be applied to a per-record read.
 *
 * `501` is included because it is the honest status for "declared, not
 * implemented"; elaborate's public plane answers `404` today (its radix router
 * simply has no such route), and both mean the same thing to a reader: not a
 * fault, not yet.
 */
export function isSurfaceNotServed(e: unknown): boolean {
  return e instanceof ApiError && (e.status === 404 || e.status === 501);
}
